import { BorderRadius, Color, Spacing } from '@persona-id/design-tokens';
import { CircleCheckIcon, CircleXIcon } from '@persona-id/design-tokens/icons';
import { XIcon } from '@persona-id/design-tokens/icons/12px';
import { DateTime } from 'luxon';
import { useEffect } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { BOX_SHADOWS } from 'shared/styles/variables';
import styled, { css, keyframes } from 'styled-components';

import Message, { MessageType } from 'dashboard/lib/interfaces/Message';
import { clearMessage } from 'dashboard/redux/actions/messages';
import ImageCircleAlert from 'images/dashboard/circle-alert.svg';

interface ToastMessageProps extends PropsFromRedux {
  message: Message;
}

const ToastMessage = ({ message, clearMessage }: ToastMessageProps) => {
  useEffect(() => {
    const expirationTimeout = DateTime.fromISO(message.expiresAt).diff(
      DateTime.local(),
      'milliseconds',
    ).milliseconds;
    const expirationTimer = setTimeout(() => {
      clearMessage(message.id);
    }, expirationTimeout);
    return () => clearTimeout(expirationTimer);
  }, [message, clearMessage]);

  switch (message.messageType) {
    case MessageType.Success:
      return <SuccessToastMessage message={message} clearMessage={clearMessage} />;
    case MessageType.Failure:
      return <FailureToastMessage message={message} clearMessage={clearMessage} />;
    case MessageType.Notice:
      return <NoticeToastMessage message={message} clearMessage={clearMessage} />;
    default:
      throw new Error('Unsupported Message Type');
  }
};

const mapDispatchToProps = {
  clearMessage,
};

const connector = connect(null, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(ToastMessage);

const bounce = keyframes`
  0%   { transform:  translateX(0); opacity: 0;}
  10%  { transform: translateX(0); }
  30%  { transform:  translateX(-25px); }
  50%  { transform:  translateX(0); }
  57%  { transform:  translateX(-4px); }
  64%  { transform:  translateX(0); }
  100% { transform:  translateX(0); opacity: 1}
`;

const sharedToastStyles = css`
  border-radius: ${BorderRadius.Lg};
  box-shadow: ${BOX_SHADOWS.card};
  padding: ${Spacing[4]};
  font-size: ${({ theme }) => theme.text.paragraph.lgBold.fontSize};
  font-weight: ${({ theme }) => theme.text.paragraph.lgBold.fontWeight};

  display: flex;
  align-items: center;

  transition: opacity;
  animation-duration: 1s;
  transform-origin: bottom;
  animation-name: ${bounce};
  animation-timing-function: ease;
  margin-bottom: ${Spacing[1]};
`;

const MessageIcon = styled('div')`
  margin-right: ${Spacing[2]};
  display: flex;
`;

const MessageBody = styled('span')`
  margin-right: ${Spacing[2]};
  flex: 1 1 auto;
  white-space: pre-wrap;
`;

export const ClearIcon = styled('div').attrs(() => ({ role: 'button' }))`
  display: flex;
  cursor: pointer;
`;

const SuccessToast = styled('div')`
  background-color: ${Color.CoreGreen400};
  color: ${Color.PrimaryBlack};

  ${sharedToastStyles} path {
    stroke: ${Color.PrimaryBlack};
  }
`;

const ICON_DIMENSION = 24;
const X_DIMENSION = 14;

export const SuccessToastMessage = ({ message, clearMessage }: ToastMessageProps) => {
  return (
    <SuccessToast data-test='toast-message__success'>
      <MessageIcon>
        <CircleCheckIcon
          color={Color.BorderIconSuccess}
          width={ICON_DIMENSION}
          height={ICON_DIMENSION}
        />
      </MessageIcon>
      <MessageBody>{message.message}</MessageBody>
      <ClearIcon data-test='clear-notifications' onClick={() => clearMessage(message.id)}>
        <XIcon width={X_DIMENSION} height={X_DIMENSION} />
      </ClearIcon>
    </SuccessToast>
  );
};

const FailureToast = styled('div')`
  background-color: ${Color.CoreRed600};
  color: ${Color.PrimaryBlack};

  ${sharedToastStyles} path {
    stroke: ${Color.PrimaryBlack};
  }
`;

export const FailureToastMessage = ({ message, clearMessage }: ToastMessageProps) => {
  return (
    <FailureToast data-test='toast-message__failure'>
      <MessageIcon>
        <CircleXIcon
          color={Color.BorderIconDanger}
          width={ICON_DIMENSION}
          height={ICON_DIMENSION}
        />
      </MessageIcon>
      <MessageBody>{message.message}</MessageBody>
      <ClearIcon data-test='clear-notifications' onClick={() => clearMessage(message.id)}>
        <XIcon width={X_DIMENSION} height={X_DIMENSION} />
      </ClearIcon>
    </FailureToast>
  );
};

const NoticeToast = styled('div')`
  background-color: ${Color.CoreYellow400};
  color: ${Color.PrimaryBlack};

  ${sharedToastStyles} path {
    stroke: ${Color.PrimaryBlack};
  }
`;

export const NoticeToastMessage = ({ message, clearMessage }: ToastMessageProps) => {
  return (
    <NoticeToast>
      <MessageIcon>
        <ImageCircleAlert width={ICON_DIMENSION} height={ICON_DIMENSION} />
      </MessageIcon>
      <MessageBody>{message.message}</MessageBody>
      <ClearIcon data-test='clear-notifications' onClick={() => clearMessage(message.id)}>
        <XIcon width={X_DIMENSION} height={X_DIMENSION} />
      </ClearIcon>
    </NoticeToast>
  );
};
